import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { updateOperationExpense } from '@/modules/Report/components/OperationExpenses/services'
import {
    OperationExpense,
    UpdateOperationExpenseRequest,
    OPERATION_EXPENSE_CATEGORIES,
} from '@/modules/Report/components/OperationExpenses/types'
import { Dialog, Button, FormItem, Typogaphy } from 'vnetwork-ui'
import { ModalProps } from '@/types/shared'

export interface UpdateOperationExpenseModalProps extends ModalProps {
    expense: OperationExpense
    refetch?: () => void
}

interface FormData {
    expense_name: string
    category: string
    amount: number
    currency: string
    notes?: string
    description?: string
}

const UpdateOperationExpenseModal: React.FC<
    UpdateOperationExpenseModalProps
> = ({ open, onClose, expense, refetch }) => {
    const { t } = useTranslation()

    // Form schema - only editable fields
    const schema = useMemo(
        () =>
            yup.object().shape({
                expense_name: yup.string().required(t('required')),
                category: yup.string().required(t('required')),
                amount: yup
                    .number()
                    .min(0, t('must_be_zero_or_positive'))
                    .required(t('required')),
                currency: yup.string().required(t('required')),
                notes: yup.string(),
                description: yup.string(),
            }),
        [t]
    )

    const defaultValues: FormData = useMemo(
        () => ({
            expense_name: expense.expense_name,
            category: expense.category,
            amount: expense.amount,
            currency: expense.currency,
            notes: expense.notes || '',
            description: expense.description || '',
        }),
        [expense]
    )

    const formControl = useForm<FormData>({
        defaultValues,
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    // Reset form when expense changes
    useEffect(() => {
        if (open) {
            reset(defaultValues)
        }
    }, [open, expense, reset, defaultValues])

    // Category options
    const categoryOptions = useMemo(
        () =>
            OPERATION_EXPENSE_CATEGORIES.map((category) => ({
                label: t(`expense_category_${category}`),
                value: category,
            })),
        [t]
    )

    // Update mutation
    const { mutate: update, isPending } = useMutationWithHandler(
        {
            mutationFn: ({
                id,
                data,
            }: {
                id: number
                data: UpdateOperationExpenseRequest
            }) => updateOperationExpense(id, data),
        },
        {
            reactQueryKeys: [QUERY_KEYS.REPORTS_BI_OPERATION_EXPENSES],
            onSuccess: () => {
                refetch?.()
                onClose()
            },
        }
    )

    const onSubmit = (values: FormData) => {
        update({
            id: expense.id,
            data: {
                expense_name: values.expense_name,
                category: values.category as any,
                amount: values.amount,
                currency: values.currency,
                notes: values.notes,
                description: values.description,
            },
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit_operation_expense')}
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
                {/* Display read-only info */}
                <div className="bg-gray-50 p-4 rounded-md space-y-2">
                    <div className="flex justify-between">
                        <Typogaphy className="text-gray-600">
                            {t('expense_month')}:
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {expense.report_month}
                        </Typogaphy>
                    </div>
                </div>

                {/* Editable fields */}
                <FormItem
                    control={control}
                    name="category"
                    type="select"
                    label={t('category')}
                    options={categoryOptions}
                    allowClear={false}
                />

                <FormItem
                    control={control}
                    name="expense_name"
                    type="textfield"
                    label={t('expense_name')}
                />

                <FormItem
                    control={control}
                    name="amount"
                    type="number"
                    label={t('amount')}
                />

                <FormItem
                    control={control}
                    name="currency"
                    type="textfield"
                    label={t('currency')}
                />

                <FormItem
                    control={control}
                    name="notes"
                    type="textarea"
                    label={t('notes')}
                />

                <FormItem
                    control={control}
                    name="description"
                    type="textarea"
                    label={t('description')}
                />

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isPending}
                        loading={isPending}
                    >
                        {t('update')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default UpdateOperationExpenseModal
